/**
 * Impersonation Banner Component
 *
 * Displays a prominent banner when an admin is viewing the app
 * as another user. Includes user info and exit button.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export const ImpersonationBanner = () => {
  const { isImpersonating, impersonatedUser, impersonatedClient, stopImpersonation } = useAuth();
  const navigate = useNavigate();

  if (!isImpersonating || !impersonatedUser) {
    return null;
  }

  const displayName = [impersonatedUser.firstName, impersonatedUser.lastName]
    .filter(Boolean)
    .join(' ') || impersonatedUser.email;

  const roleLabel = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    viewer: 'Viewer',
  }[impersonatedUser.role];

  const handleExitImpersonation = () => {
    const returnPath = stopImpersonation();
    if (returnPath) {
      navigate(returnPath);
    }
  };

  return (
    <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between shadow-md z-50">
      <div className="flex items-center gap-3">
        <EyeIcon />
        <span className="font-medium">
          Viewing as:{' '}
          <strong>{displayName}</strong>
          {impersonatedClient && (
            <span className="text-amber-800">
              {' '}({impersonatedClient.name})
            </span>
          )}
          <span className="ml-2 px-2 py-0.5 bg-amber-600/30 rounded text-xs font-semibold">
            {roleLabel}
          </span>
        </span>
      </div>

      <button
        onClick={handleExitImpersonation}
        className="flex items-center gap-2 px-3 py-1.5 bg-amber-700 hover:bg-amber-800 text-white rounded-lg text-sm font-medium transition-colors"
      >
        <ExitIcon />
        Exit Impersonation
      </button>
    </div>
  );
};

/**
 * Eye icon
 */
const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

/**
 * Exit/X icon
 */
const ExitIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default ImpersonationBanner;
